import type { JunctionOverlap, PlacedWall, Vec2 } from './types.js';

/**
 * Phase 2 decision: walls are placed exactly as drawn and their brick volumes
 * are allowed to interpenetrate where they meet. Every brick in the assembly
 * is a brick that was actually laid, in the position it was laid.
 *
 * This is the identity function, and it exists so that it is the ONLY place
 * that has to change if trimming or mitring is wanted later. A junction policy
 * then becomes one pure function with its own tests rather than a change that
 * reaches into the renderer.
 */
export function resolveJunctions(walls: readonly PlacedWall[]): readonly PlacedWall[] {
  return walls;
}

/**
 * Where two walls claim the same plan area.
 *
 * Nothing acts on this in phase 2 — it is review information. The plan view
 * marks the overlaps so the corner z-fighting in a later 3D view is something
 * the drawing already told you about, rather than a surprise.
 */
export function findOverlaps(walls: readonly PlacedWall[]): JunctionOverlap[] {
  const out: JunctionOverlap[] = [];
  for (let i = 0; i < walls.length; i++) {
    const a = walls[i] as PlacedWall;
    if (a.planCorners.length < 3) continue;
    for (let j = i + 1; j < walls.length; j++) {
      const b = walls[j] as PlacedWall;
      if (b.planCorners.length < 3) continue;
      const region = intersectPolygons(a.planCorners, b.planCorners);
      const overlap = Math.abs(area(region));
      if (overlap > 1) {
        out.push({ a: a.id, b: b.id, areaMm2: overlap, at: centroid(region) });
      }
    }
  }
  return out;
}

/** Area shared by two convex polygons. */
export function intersectionArea(subject: readonly Vec2[], clip: readonly Vec2[]): number {
  return Math.abs(area(intersectPolygons(subject, clip)));
}

/**
 * The region shared by two convex polygons, by Sutherland–Hodgman clipping.
 * Wall plan rectangles are always convex, which is what makes this safe.
 */
export function intersectPolygons(
  subject: readonly Vec2[],
  clip: readonly Vec2[],
): Vec2[] {
  let output: Vec2[] = [...subject];
  const orientation = Math.sign(area(clip));
  if (orientation === 0) return [];

  for (let i = 0; i < clip.length && output.length > 0; i++) {
    const a = clip[i] as Vec2;
    const b = clip[(i + 1) % clip.length] as Vec2;
    const input = output;
    output = [];
    for (let k = 0; k < input.length; k++) {
      const current = input[k] as Vec2;
      const previous = input[(k + input.length - 1) % input.length] as Vec2;
      const currentIn = side(a, b, current) * orientation >= 0;
      const previousIn = side(a, b, previous) * orientation >= 0;
      if (currentIn) {
        if (!previousIn) output.push(cross(previous, current, a, b));
        output.push(current);
      } else if (previousIn) {
        output.push(cross(previous, current, a, b));
      }
    }
  }
  return output;
}

/** Centre of a polygon's vertices — enough to point a marker at it. */
function centroid(ring: readonly Vec2[]): Vec2 {
  if (ring.length === 0) return { x: 0, z: 0 };
  let x = 0;
  let z = 0;
  for (const corner of ring) {
    x += corner.x;
    z += corner.z;
  }
  return { x: x / ring.length, z: z / ring.length };
}

function side(a: Vec2, b: Vec2, p: Vec2): number {
  return (b.x - a.x) * (p.z - a.z) - (b.z - a.z) * (p.x - a.x);
}

function cross(p1: Vec2, p2: Vec2, a: Vec2, b: Vec2): Vec2 {
  const d1x = p2.x - p1.x;
  const d1z = p2.z - p1.z;
  const d2x = b.x - a.x;
  const d2z = b.z - a.z;
  const denominator = d1x * d2z - d1z * d2x;
  if (Math.abs(denominator) < 1e-12) return p2;
  const t = ((a.x - p1.x) * d2z - (a.z - p1.z) * d2x) / denominator;
  return { x: p1.x + d1x * t, z: p1.z + d1z * t };
}

function area(ring: readonly Vec2[]): number {
  let total = 0;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i] as Vec2;
    const b = ring[(i + 1) % ring.length] as Vec2;
    total += a.x * b.z - b.x * a.z;
  }
  return total / 2;
}
